import CustomScrollbar from 'app/components/CustomScrollbar';
import CloseButton from 'app/components/Header/CloseButton';
import { motion, Variants } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { cx } from '@twind/core';

import TailorImportantInformation from '../tailor/components/TailorImportantInformation';
import useContactFormISI from './hooks/useContactFormISI';
import useContactFormLogic from './hooks/useContactFormLogic';
import ContactFormContent from './components/ContactFormContent';
import useContactFormTracking from './hooks/useContactFormTracking';
import { useContactFormState } from './stores/useContactFormState';

const variants: Variants = {
  initial: {
    y: 'var(--app-height)',
  },
  animate: ({ isOpen }) => ({
    y: isOpen ? 0 : 'var(--app-height)',
    transition: { duration: 0.15, delay: isOpen ? 0.15 : 0 },
  }),
};

const overlayVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: ({ isOpen }) => ({
    opacity: isOpen ? 1 : 0,
    transition: { duration: 0.15, delay: isOpen ? 0.15 : 0 },
  }),
};

export default function ContactForm() {
  const { captureContactFormClosed } = useContactFormTracking();
  const { closeContactForm, contactFormSlug } = useContactFormState();
  const [isOpen, setIsOpen] = useState(true);
  const [infoHeight, setInfoHeight] = useState(0);
  const infoRef = useRef<HTMLButtonElement>(null);
  const showISI = useContactFormISI();

  const handleClose = () => {
    setIsOpen(false);
    captureContactFormClosed(currentPage, contactFormSlug);
  };

  const { currentPage, totalPages, customization } = useContactFormLogic({ onClose: handleClose, contactFormSlug });

  const handleAnimationComplete = () => {
    if (!isOpen) {
      closeContactForm();
    }
  };

  useEffect(() => {
    if (!infoRef.current) return;

    const updateHeight = () => {
      setInfoHeight(infoRef.current?.offsetHeight || 0);
    };

    const mutationObserver = new MutationObserver(updateHeight);
    const resizeObserver = new ResizeObserver(updateHeight);

    mutationObserver.observe(infoRef.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    resizeObserver.observe(infoRef.current);

    setTimeout(updateHeight, 50);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="absolute inset-0 z-50">
      <motion.div
        custom={{ isOpen }}
        initial="initial"
        animate="animate"
        variants={overlayVariants}
        className="absolute inset-0 bg-[#4C5050]/[.08]"
      />
      {showISI && (
        <TailorImportantInformation
          ref={infoRef}
          className="absolute top-0 left-0"
        />
      )}
      <div
        className="absolute inset-x-0 bottom-0 flex flex-col"
        style={{ height: showISI ? `calc(100% - ${infoHeight}px)` : 'calc(100% - 10px)' }}
      >
        <motion.div
          custom={{ isOpen }}
          initial="initial"
          animate="animate"
          variants={variants}
          onAnimationComplete={handleAnimationComplete}
          className="flex flex-col h-full"
        >
          <div
            className={cx(
              '~(p-[24px] contact-form-header flex justify-between items-center bg-[#4C5050]/[.08])',
              showISI ? 'rounded-none' : ''
            )}
          >
            {customization?.contact_forms_header_page_template ? (
              <span className="flex items-center text-sm text-center justify-center grow">
                {customization?.contact_forms_header_page_template
                  ?.replace(/{current}/g, currentPage.toString())
                  .replace(/{total}/g, totalPages.toString())}
              </span>
            ) : null}

            <CloseButton
              onClick={handleClose}
              className="w-[32px] h-[32px] ml-auto"
              circleClassName="w-[24px] h-[24px] bg-[#4C5050]/[.08]"
              id="ostro-contact-form-close-button"
              ariaLabel="close contact form"
            />
          </div>

          <CustomScrollbar className="flex flex-col grow px-[24px] pt-[32px] bg-white">
            <ContactFormContent
              onClose={handleClose}
              className="flex flex-col h-full"
            />
          </CustomScrollbar>
        </motion.div>
      </div>
    </div>
  );
}
